import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { Getters } from 'src/store/display';
import { useDebounce } from 'src/utils/hooks';

import TimelineAppliedFilters, {
  TimelineAppliedFilterField,
} from './components/TimelineAppliedFilters';
import TimelineCard from './components/TimelineCard';
import TimelineCardSkeleton from './components/TimelineCardSkeleton';
import TimelineContext from './components/TimelineContext';
import TimelineDetailsDrawer from './components/TimelineDetailsDrawer';
import TimelineFutureCalendar from './components/TimelineFutureCalendar';
import TimelineKpis from './components/TimelineKpis';
import TimelinePagination from './components/TimelinePagination';
import TimelineToolbar from './components/TimelineToolbar';
import useTimelineQueries from './hooks/useTimelineQueries';
import { getTimelineFilterOptions, getTimelineSummary } from './services';
import {
  EMPTY_TIMELINE_FILTERS,
  getTimelineDefaultSort,
  shouldShowTimelineEmptyState,
  TimelineTab,
} from './timelineData';
import {
  parseTimelineUrlState,
  serializeTimelineUrlState,
  TimelineUrlState,
} from './timelineUrlState';

import type { TimelineRecord } from 'src/types/convocation-summary';

export interface TimelineExperienceProps {
  operationId?: string;
  hasPermission?: boolean;
}

const TIMELINE_TAB_TITLES: Record<TimelineTab, string> = {
  highlights: 'Destaques',
  future: 'Eventos futuros',
  all: 'Todos os registros',
};

export default function TimelineExperience({
  operationId,
  hasPermission: hasPermissionOverride,
}: TimelineExperienceProps) {
  const [selectedRecord, setSelectedRecord] = useState<TimelineRecord | null>(
    null,
  );
  const { auth } = useAuthContext();
  const operationScope = operationId?.trim() || null;
  const headerBannerMessage = useSelector(Getters.bannerMessage);
  const demoBannerHeight = auth.periodDemo?.isDemo ? 72 : 0;
  const messageBannerHeight = headerBannerMessage.message ? 50 : 0;
  const navbarAdditionalHeight = demoBannerHeight + messageBannerHeight;
  const history = useHistory();
  const location = useLocation();
  const urlState = useMemo(
    () => parseTimelineUrlState(location.search, Boolean(operationId)),
    [location.search, operationId],
  );
  useEffect(() => {
    setSelectedRecord(null);
  }, [operationId]);
  const hasPermission =
    hasPermissionOverride ??
    !!auth.user?.subscription?.plan?.permissions?.linha_do_tempo;
  const search = useDebounce(urlState.search.trim(), 500);
  const isSearchPending = search !== urlState.search.trim();
  const synchronizedSearch = serializeTimelineUrlState(
    location.search,
    urlState,
    Boolean(operationId),
  );
  useEffect(() => {
    const currentSearch = location.search.replace(/^\?/, '');
    if (synchronizedSearch === currentSearch) return;

    history.replace({
      pathname: location.pathname,
      search: synchronizedSearch ? `?${synchronizedSearch}` : '',
      hash: location.hash,
      state: location.state,
    });
  }, [
    history,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    synchronizedSearch,
  ]);
  const replaceUrlState = useCallback(
    (nextState: Partial<TimelineUrlState>) => {
      const nextSearch = serializeTimelineUrlState(
        location.search,
        {
          ...urlState,
          ...nextState,
        },
        Boolean(operationId),
      );
      const currentSearch = location.search.replace(/^\?/, '');
      if (nextSearch === currentSearch) return;

      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
        state: location.state,
      });
    },
    [
      history,
      location.hash,
      location.pathname,
      location.search,
      location.state,
      operationId,
      urlState,
    ],
  );
  const {
    data: filterOptions,
    isError: isFilterOptionsError,
    isLoading: isFilterOptionsLoading,
    refetch: retryFilterOptions,
  } = useQuery(
    ['timeline-filter-options', operationScope],
    ({ signal }) => getTimelineFilterOptions(signal, operationId),
    { enabled: hasPermission, staleTime: 5 * 60 * 1000 },
  );
  const {
    data: timelineSummary,
    isError: isTimelineSummaryError,
    isLoading: isTimelineSummaryLoading,
    refetch: retryTimelineSummary,
  } = useQuery(
    ['timeline-summary', operationScope, auth.user?.id],
    ({ signal }) => getTimelineSummary(signal, operationId),
    { enabled: hasPermission, staleTime: 30_000 },
  );
  const sort = urlState.sort ?? getTimelineDefaultSort(urlState.activeTab);
  const {
    baseTotal,
    cancelAuxiliaryCount,
    displayedPage,
    displayedPageSize,
    isError,
    isFetching,
    isLoading,
    isCriteriaTransitioning,
    normalizedPage,
    occurredCount,
    records,
    retry,
    showListSkeleton,
    tabCounts,
    total,
  } = useTimelineQueries({
    hasPermission,
    operationId,
    userId: auth.user?.id ?? null,
    activeTab: urlState.activeTab,
    pageIndex: urlState.page - 1,
    pageSize: urlState.pageSize,
    search,
    isSearchPending,
    filters: urlState.filters,
    sort,
  });
  useEffect(() => {
    if (normalizedPage === undefined || normalizedPage === urlState.page) {
      return;
    }

    replaceUrlState({ page: normalizedPage });
  }, [normalizedPage, replaceUrlState, urlState.page]);
  const applyFilters = (filters: TimelineUrlState['filters']) =>
    replaceUrlState({ filters, page: 1 });
  const clearAllFilters = () => {
    replaceUrlState({
      search: '',
      filters: EMPTY_TIMELINE_FILTERS,
      page: 1,
    });
  };
  const removeAppliedFilter = (
    field: TimelineAppliedFilterField,
    value?: string,
  ) => {
    if (field === 'search') {
      replaceUrlState({ search: '', page: 1 });
    } else if (
      field === 'markets' ||
      field === 'categories' ||
      field === 'types' ||
      field === 'species' ||
      field === 'themes' ||
      field === 'relevances'
    ) {
      replaceUrlState({
        filters: {
          ...urlState.filters,
          [field]: urlState.filters[field].filter((item) => item !== value),
        },
        page: 1,
      });
    } else if (field === 'dateStart' || field === 'dateEnd') {
      replaceUrlState({
        filters: { ...urlState.filters, [field]: '' },
        page: 1,
      });
    } else {
      replaceUrlState({
        filters: { ...urlState.filters, favorites: false },
        page: 1,
      });
    }
  };
  const hasAppliedQuery =
    Boolean(urlState.search.trim()) ||
    (!operationId && urlState.filters.markets.length > 0) ||
    urlState.filters.categories.length > 0 ||
    urlState.filters.types.length > 0 ||
    urlState.filters.species.length > 0 ||
    urlState.filters.themes.length > 0 ||
    urlState.filters.relevances.length > 0 ||
    Boolean(urlState.filters.dateStart) ||
    Boolean(urlState.filters.dateEnd) ||
    (!operationId && urlState.filters.favorites);
  const emptyStateMessage =
    baseTotal === 0
      ? 'Ainda não há registros disponíveis na Linha do Tempo.'
      : hasAppliedQuery
        ? 'Nenhum registro corresponde à busca e aos filtros aplicados.'
        : baseTotal === 'loading'
          ? 'Verificando registros disponíveis na Linha do Tempo…'
          : baseTotal === 'unavailable'
            ? 'Não foi possível confirmar os registros disponíveis na Linha do Tempo.'
            : urlState.activeTab === 'all'
              ? 'Nenhum registro encontrado na Linha do Tempo.'
              : 'Nenhum registro encontrado nesta visão.';
  const renderTabLabel = (tab: TimelineTab, label: string) => {
    const count = tabCounts[tab];
    const countLabel =
      count === 'loading' ? (
        <Skeleton
          data-testid={`timeline-tab-${tab}-loading`}
          variant="rounded"
          width={24}
          height={16}
        />
      ) : count === 'unavailable' ? (
        '—'
      ) : (
        count
      );

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <span>{label}</span>
        <Chip size="small" label={countLabel} />
      </Stack>
    );
  };
  const activeCount = tabCounts[urlState.activeTab];
  const resultsDetail =
    activeCount === 'loading' ? (
      <Skeleton
        data-testid="timeline-results-loading"
        variant="text"
        width={160}
        height={24}
      />
    ) : activeCount === 'unavailable' ? (
      'Contagem indisponível'
    ) : (
      `${activeCount} registros encontrados`
    );
  const omittedCount =
    urlState.activeTab === 'highlights' &&
    !isLoading &&
    !isFetching &&
    !isError &&
    typeof occurredCount === 'number' &&
    typeof tabCounts.highlights === 'number'
      ? occurredCount - tabCounts.highlights
      : 0;
  const selectTab = (tab: TimelineTab) => {
    cancelAuxiliaryCount(tab);
    replaceUrlState({ activeTab: tab, page: 1 });
  };

  if (!hasPermission) return null;

  const timelineContent = (
    <Stack spacing={3} data-testid="timeline-content">
      <TimelineKpis
        summary={timelineSummary}
        isLoading={isTimelineSummaryLoading}
        isError={isTimelineSummaryError}
        onRetry={() => {
          void retryTimelineSummary();
        }}
      />
      <Tabs
        value={urlState.activeTab}
        onChange={(_, value: TimelineTab) => selectTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Visões da linha do tempo">
        <Tab
          value="highlights"
          label={renderTabLabel('highlights', 'Destaques')}
        />
        <Tab
          value="future"
          label={renderTabLabel('future', 'Eventos futuros')}
        />
        <Tab value="all" label={renderTabLabel('all', 'Todos os registros')} />
      </Tabs>
      {urlState.activeTab === 'highlights' && (
        <Typography variant="body2" color="text.secondary">
          Eventos já ocorridos com relevância Alta.
          {omittedCount > 0 && (
            <>
              {' '}
              {omittedCount} de relevância Média, Baixa ou não informada estão
              em{' '}
              <Button
                variant="text"
                size="small"
                onClick={() => selectTab('all')}
                sx={{
                  p: 0,
                  minWidth: 0,
                  verticalAlign: 'baseline',
                  fontSize: 'inherit',
                  textTransform: 'none',
                }}>
                Todos os registros
              </Button>
            </>
          )}
        </Typography>
      )}
      {urlState.activeTab === 'future' && (
        <Typography variant="body2" color="text.secondary">
          Próximos eventos, de todas as relevâncias.
        </Typography>
      )}
      <TimelineToolbar
        searchValue={urlState.search}
        onSearchChange={(searchValue) =>
          replaceUrlState({ search: searchValue, page: 1 })
        }
        activeTab={urlState.activeTab}
        sort={sort}
        onSortChange={(sort) => replaceUrlState({ sort, page: 1 })}
        appliedFilters={urlState.filters}
        onApplyFilters={applyFilters}
        onClearAll={clearAllFilters}
        filterOptions={filterOptions}
        filterOptionsLoading={isFilterOptionsLoading}
        filterOptionsError={isFilterOptionsError}
        onRetryFilterOptions={() => {
          void retryFilterOptions();
        }}
        canFilterFavorites={auth.isLoggedIn}
        navbarAdditionalHeight={navbarAdditionalHeight}
        operationScoped={Boolean(operationId)}
      />
      {urlState.activeTab === 'future' && (
        <TimelineFutureCalendar
          operationId={operationId}
          userId={auth.user?.id ?? null}
          search={search}
          isSearchPending={isSearchPending}
          filters={urlState.filters}
          onDateRangeChange={(range) =>
            replaceUrlState({
              filters: { ...urlState.filters, ...range },
              page: 1,
            })
          }
        />
      )}
      <TimelineAppliedFilters
        search={urlState.search}
        filters={urlState.filters}
        onRemove={removeAppliedFilter}
        operationScoped={Boolean(operationId)}
      />
      <Stack spacing={1}>
        <Divider data-testid="timeline-results-divider" />
        <TimelineContext
          title={TIMELINE_TAB_TITLES[urlState.activeTab]}
          detail={resultsDetail}
        />
        <Box sx={{ height: 4 }}>
          {isCriteriaTransitioning && (
            <LinearProgress aria-label="Atualizando a lista" />
          )}
        </Box>
      </Stack>
      <Stack
        spacing={1.5}
        data-testid="timeline-list-region"
        aria-busy={showListSkeleton || isCriteriaTransitioning}>
        {showListSkeleton && (
          <Stack spacing={1.5} data-testid="timeline-loading">
            {Array.from({ length: 3 }).map((_, index) => (
              <TimelineCardSkeleton key={index} />
            ))}
          </Stack>
        )}
        {isError && (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button color="inherit" size="small" onClick={() => retry()}>
                Tentar novamente
              </Button>
            }>
            Não foi possível carregar a Linha do Tempo. Tente novamente em
            instantes.
          </Alert>
        )}
        <Stack
          spacing={1.5}
          data-testid="timeline-card-list"
          sx={(theme) => ({
            opacity: isCriteriaTransitioning
              ? theme.palette.action.disabledOpacity
              : 1,
            pointerEvents: isCriteriaTransitioning ? 'none' : 'auto',
          })}>
          {!showListSkeleton &&
            records.map((record) => (
              <TimelineCard
                key={record.id}
                record={record}
                onOpenDetails={setSelectedRecord}
                operationScoped={Boolean(operationId)}
              />
            ))}
        </Stack>
        {shouldShowTimelineEmptyState({
          isLoading: isLoading || showListSkeleton || isCriteriaTransitioning,
          isError,
          visibleCount: records.length,
        }) && (
          <Stack role="status" spacing={1} alignItems="flex-start">
            <Box>{emptyStateMessage}</Box>
            {hasAppliedQuery && (
              <Button size="small" onClick={clearAllFilters}>
                Limpar busca e filtros
              </Button>
            )}
          </Stack>
        )}
      </Stack>
      {typeof total === 'number' ? (
        <TimelinePagination
          page={displayedPage}
          pageSize={displayedPageSize}
          total={total}
          onPageChange={(page) => replaceUrlState({ page })}
          onPageSizeChange={(pageSize) =>
            replaceUrlState({ page: 1, pageSize })
          }
        />
      ) : total === 'loading' ? (
        <Skeleton
          data-testid="timeline-pagination-loading"
          width="100%"
          height={40}
        />
      ) : (
        <Box role="status" aria-label="Paginação indisponível">
          —
        </Box>
      )}
      <Alert severity="info" sx={{ mt: 1 }}>
        <strong>Aviso:</strong> Estes resumos foram gerados por IA e podem não
        refletir todos os detalhes dos documentos originais. Recomenda-se
        consultar o documento completo para informações precisas.
      </Alert>
    </Stack>
  );

  return (
    <>
      {operationId ? (
        timelineContent
      ) : (
        <Paper
          data-testid="timeline-paper"
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            pt: { xs: 3, md: 4 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}>
          {timelineContent}
        </Paper>
      )}
      <TimelineDetailsDrawer
        open={Boolean(selectedRecord)}
        record={selectedRecord}
        navbarAdditionalHeight={navbarAdditionalHeight}
        operationId={operationId}
        operationScoped={Boolean(operationId)}
        onClose={() => setSelectedRecord(null)}
      />
    </>
  );
}
